import { unsafeCoerce } from 'fp-ts/lib/function';

/**
 * A value which obeys three constraints:
 * - Unless it is null or undefined, it may be treated as an object.
 * - When treated as an object, any property may be read, even if it is not defined.
 * - The value of any property also obeys these constraints.
 *
 * These are trivially true for all JS values at runtime, so any value can be safely cast to Dynamic.
 *
 * When chained with the `?.` operator, this can be used to deeply inspect unknown values.
 *
 * This is very much like `any` except:
 * - It can't be silently cast to any other value
 * - one has to check whether it is `null` or `undefined`
 */
export type Dynamic = undefined | null | DynamicObject | DynamicPrimitive;

type Primitive = number | string | boolean;
type DynamicPrimitive = Primitive & DynamicObject;

interface DynamicObject {
  [key: string]: Dynamic & Primitive;
}

/** Safely cast an unknown value to a Dynamic
 * @see Dynamic
 */
export const asDynamic = (value: unknown): Dynamic => unsafeCoerce(value);

export const parseDynamicFromJsonString = (text: unknown): Dynamic => {
  try {
    const parsed: unknown = JSON.parse(String(text));
    return asDynamic(parsed);
  } catch (err) {
    return undefined;
  }
};
